/*
  FD brand: the gallery's full-screen view.

  It owns no images. Opening it copies the <img> off whichever slide is on the
  stage and asks the browser for the 100vw candidate of the srcset that slide
  already carries, so a full-screen look costs one more fetch of one picture
  and no second set of markup.

  It also owns no filtering. Prev/next walk the slides that are NOT `hidden`,
  which is the list fd-gallery.js has already narrowed to the chosen colour and
  hand -- so the full-screen view can never show a fabric or a chaise side the
  page underneath is not showing, without knowing either rule. The dimension
  PLATES join that walk: a lettered technical drawing is exactly the kind of
  picture worth enlarging. What stays out is everything that is wording or
  layout rather than a picture -- the phone's specs slides, the stand-in and
  companion tiles, and a panel still waiting for its drawing -- because blowing
  one of those up would show a fragment of a slide that is really a table. One
  resolver, lbImg(), is the single place that decides.

  The shell (Escape, the Tab trap, the scroll lock, returning focus) is the
  drawer's, borrowed from window.FdChooser.focusables and from the same
  `fd-oc-locked` class -- but not window.FdChooser.shell itself, whose contract
  is a row button that toggles a scrim and a panel by class. Here the overlay is
  shown by the `hidden` attribute and opening has to paint a picture first, so
  the behaviour is mirrored rather than reused.
*/
if (!customElements.get('fd-lightbox')) {
  /* Gesture and cue tuning, in one place because these numbers are a feel, not
     a rule: the commit distance is a share of the stage so it scales with the
     screen, and the flick speed is what lets a short, fast pull still commit. */
  const FD_LB_COMMIT_RATIO = 0.28; // share of the stage width that commits
  const FD_LB_FLICK = 0.5; // px/ms that commits however short the pull
  const FD_LB_AXIS_LOCK = 8; // px of travel that decides horizontal vs vertical
  const FD_LB_CLOSE_DROP = 110; // px of downward pull that closes on release
  const FD_LB_SLIDE_MS = 240; // commit animation
  const FD_LB_CLOSE_MS = 200; // the committed pull-down's exit
  const FD_LB_SWIPE_MS = 4200; // swipe cue on screen
  const FD_LB_ROTATE_MS = 5200; // rotate cue on screen
  const FD_LB_NUDGE_MS = 3200; // two nudge cycles plus the delay
  const FD_LB_KEY_SWIPE = 'fd:lb:swipe-learnt';
  const FD_LB_KEY_ROTATE = 'fd:lb:rotate-learnt';
  /* Zoom, which exists for one picture in particular: a dimension plate whose
     A--F legend is unreadable at 375px. The cap is where a plate's lettering is
     comfortably large without turning the photographs to mush; the snap is what
     lets a lazy pinch back in land on exactly 1, because anything above 1 keeps
     the swipe and the pull-to-close switched off. */
  const FD_LB_ZOOM_MAX = 4;
  const FD_LB_ZOOM_DBL = 2.5; // double-tap target scale
  const FD_LB_ZOOM_SNAP = 1.02; // at or below this, back to exactly 1
  const FD_LB_DBL_MS = 300; // two taps this close together are one gesture
  const FD_LB_DBL_SLOP = 24; // px the second tap may land from the first

  /* A cue that has done its job never comes back -- and a browser that will not
     answer about storage (private mode, blocked cookies) simply means the cue
     keeps showing, which is the harmless way to be wrong. */
  function fdLbLearnt(key) {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch (error) {
      return false;
    }
  }

  function fdLbLearn(key) {
    try {
      window.localStorage.setItem(key, '1');
    } catch (error) {
      /* fail open */
    }
  }

  customElements.define(
    'fd-lightbox',
    class FdLightbox extends HTMLElement {
      connectedCallback() {
        // open() portals this element to <body> (see there for why), which
        // re-fires this callback with the gallery no longer an ancestor. The
        // wiring below is done once and survives the move, so the second run
        // must not re-read the ancestry or bind a second set of listeners.
        if (this._wired) return;
        // Read before any portal, while the element is still in its section.
        this.sectionId = this.closest('.shopify-section')?.id || null;
        this.gallery = this.closest('fd-gallery');
        this.fig = this.querySelector('[data-fd-lb-img]');
        this.opener = this.gallery?.querySelector('[data-fd-gallery-expand]');
        if (!this.gallery || !this.fig || !this.opener) return;

        this.list = this.gallery.querySelector('[id^="Slider-Gallery"]');
        this.count = this.querySelector('[data-fd-lb-count]');
        // The viewer's own thumb strip (2026-09-07). Present only in ig mode --
        // Liquid prints the <ul> and the class together, so one test covers it.
        this.rail = this.querySelector('[data-fd-lb-rail]');
        this.igRail = this.classList.contains('fd-lightbox--ig') && !!this.rail;
        this.prev = this.querySelector('[data-fd-lb-prev]');
        this.next = this.querySelector('[data-fd-lb-next]');
        this.isOpen = false;
        this.lastFocus = null;
        this.slide = null;
        this.stage = this.fig;
        this.scrim = this.querySelector('[data-fd-lb-scrim]');
        this.hintRotate = this.querySelector('[data-fd-lb-hint="rotate"]');
        this.hintSwipe = this.querySelector('[data-fd-lb-hint="swipe"]');
        this.prevBuf = null;
        this.cur = null;
        this.nextBuf = null;
        this.animating = false;
        this.drag = null;
        // Zoom is off until a pinch or a double-tap turns it on, and every path
        // that changes which picture is on the stage turns it off again.
        this.zoom = { s: 1, tx: 0, ty: 0 };
        this.pointers = new Map();
        this.pinch = null;
        this.lastTap = null;
        this.gesturesOn = false;
        this.stepTimer = null;
        this.timers = [];
        this.mqCoarse = window.matchMedia('(pointer: coarse)');
        this.mqPortrait = window.matchMedia('(orientation: portrait)');
        this.mqStill = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onKeydown = (event) => this.keydown(event);
        this.onResize = () => {
          if (!this.isOpen) return;
          // The stage width is what the strip is placed against, so a rotate or
          // a URL-bar collapse has to re-place it or the neighbours drift. A
          // rotate also re-fits the picture, so a pan measured against the old
          // fit is pointing at nothing and goes with it.
          this.resetZoom();
          this.place(0, 0);
          this.positionHints();
        };
        this.onOrient = () => {
          if (!this.isOpen) return;
          // Turning the phone IS the rotate cue answered.
          this.learn('rotate');
          this.onResize();
        };
        this.opener.addEventListener('click', () => this.open());
        this.querySelector('[data-fd-lb-scrim]')?.addEventListener('click', () => this.close());
        this.querySelector('[data-fd-lb-close]')?.addEventListener('click', () => this.close());
        this.prev?.addEventListener('click', () => this.step(-1));
        this.next?.addEventListener('click', () => this.step(1));
        // Delegated: the cells are rebuilt on every open, so nothing may hold a
        // reference to one. paint() is the existing promotion path, and it
        // resets the zoom for us.
        this.rail?.addEventListener('click', (event) => {
          const button = event.target.closest('[data-fd-lb-go]');
          if (!button) return;
          const photos = this.photos();
          const target = photos.find((slide) => slide.dataset.mediaId === button.dataset.fdLbGo);
          if (target) this.paint(target, photos);
        });
        // Additive, and last: the chips, the keys and the scrim are already
        // wired above, so a throw in here costs the drag and nothing else.
        try {
          this.wireGestures();
        } catch (error) {
          this.gesturesOn = false;
        }
        this._wired = true;
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        document.documentElement.classList.remove('fd-oc-locked');
      }

      // Which picture a slide contributes to the full-screen view. A photo
      // slide is its one <img>. A dims slide is the visible drawing inside
      // its visible panel -- a Size product keeps every size's panel in the
      // slide and a handed set carries two arts, so a bare querySelector
      // could clone a hidden LH plate. Specs slides, the stand-in tiles and
      // a pending "drawing to come" panel resolve to null and stay out.
      lbImg(slide) {
        if (slide.classList.contains('fd-gal__slide--specs')) return null;
        if ('renderPending' in slide.dataset || 'companionPending' in slide.dataset) return null;
        if ('fdDimPanels' in slide.dataset) {
          return slide.querySelector('[data-size-value]:not([hidden]) [data-fd-dim-art]:not([hidden]) img');
        }
        if (slide.classList.contains('fd-gal__slide--dims')) return null;
        return slide.querySelector('img');
      }

      // The pictures currently on show, in stage order. `hidden` is the
      // filter's own output, so this list follows the colour and the hand for
      // free; lbImg is what decides whether a slide has a picture at all.
      photos() {
        if (!this.list) return [];
        return Array.from(this.list.querySelectorAll('[data-media-id]')).filter(
          (slide) => !slide.hidden && this.lbImg(slide)
        );
      }

      open() {
        if (this.isOpen) return;
        const photos = this.photos();
        if (!photos.length) return;
        // fd-gallery's container-type creates a fixed-position containing block,
        // so the lightbox must live at body level for inset:0 to mean the
        // viewport (see the z-layer scale in fd-header.css). Done on open, not
        // on connect, so the markup stays with the gallery it belongs to until
        // it is actually needed.
        if (this.parentElement !== document.body) document.body.appendChild(this);
        this.isOpen = true;
        this.lastFocus = document.activeElement;
        if (this.igRail) this.buildRail(photos);
        this.paint(photos.find((slide) => slide.classList.contains('is-active')) || photos[0], photos);
        this.hidden = false;
        document.documentElement.classList.add('fd-oc-locked');
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.onResize);
        this.mqPortrait.addEventListener('change', this.onOrient);
        this.showHints();
        // The overlay is laid out on this same tick (its visibility is stepped,
        // not interpolated) so focus lands immediately; the extra frame is a
        // backstop for engines that defer the style recalc.
        this.focusFirst();
        requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.focusFirst();
        });
      }

      close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('resize', this.onResize);
        this.mqPortrait.removeEventListener('change', this.onOrient);
        this.clearHints();
        this.drag = null;
        this.resetZoom();
        // Fingers that were down when this closed have no up to come: the map
        // would otherwise make the next single touch look like a second one.
        this.pointers.clear();
        this.lastTap = null;
        this.animating = false;
        // A slide started just before this close would otherwise settle after
        // the next open and paint the picture they left, over the one they
        // just asked for.
        if (this.stepTimer !== null) window.clearTimeout(this.stepTimer);
        this.stepTimer = null;
        this.glide(false);
        this.place(0, 0);
        this.fadeScrim(null);
        // The strip is torn down, not kept: paint() promotes buffers by media
        // id, and a buffer surviving a close could be promoted after the page
        // underneath changed which art that id shows (a handed set's other
        // plate). An empty stage has nothing stale to offer.
        this.stage.replaceChildren();
        this.prevBuf = null;
        this.cur = null;
        this.nextBuf = null;
        // Same reason the buffers go: photos() follows the colour and hand
        // filter, so a strip kept across a variant change would offer frames
        // the page behind no longer shows.
        if (this.rail) {
          this.rail.replaceChildren();
          this.rail.hidden = true;
        }
        this.hidden = true;
        document.documentElement.classList.remove('fd-oc-locked');
        // Whichever picture the shopper stepped to in here is the one they
        // expect to find on the stage behind it.
        if (this.slide && typeof this.gallery.show === 'function') this.gallery.show(this.slide.dataset.mediaId);
        (this.lastFocus && document.contains(this.lastFocus) ? this.lastFocus : this.opener).focus();
      }

      /* A committed pull-down finishes the gesture it started: the picture
         keeps going, off the bottom and accelerating (ease-in -- it was
         already moving), while the backdrop clears -- and only then does the
         shell close do its usual work. Before this the release simply blinked
         the overlay away mid-gesture. Reduced motion gets the same end state
         immediately, as everywhere else. The delay rides this.stepTimer,
         which close() already clears, so an Escape or a chip tap during the
         drop can neither close twice nor close a NEXT open. */
      closeSlide() {
        if (this.mqStill.matches || this.stepTimer !== null) {
          this.close();
          return;
        }
        this.animating = true;
        [this.prevBuf, this.cur, this.nextBuf].forEach((buffer) => {
          if (buffer) buffer.style.transition = `transform ${FD_LB_CLOSE_MS}ms ease-in`;
        });
        this.place(0, this.stageHeight());
        if (this.scrim) {
          this.scrim.style.transition = `opacity ${FD_LB_CLOSE_MS}ms ease-out`;
          this.scrim.style.opacity = '0';
        }
        this.stepTimer = window.setTimeout(() => {
          this.stepTimer = null;
          this.animating = false;
          this.close();
        }, FD_LB_CLOSE_MS + 20);
      }

      step(direction) {
        // A finger is mid-pull: an arrow key or a chip tap arriving now would
        // repaint the buffers out from under the live drag, which would then
        // commit a second step on release -- two photographs for one gesture.
        // The drag is the gesture that was asked for, so the key is dropped.
        // (A committing drag clears this.drag before it calls step.) The
        // animating check is not just the multi-photo swallow in animateStep
        // repeated: with a single photograph there are no buffers, so that
        // path would fall through to an instant repaint -- which, landing
        // during closeSlide's drop, snapped the leaving picture back.
        if (this.drag || this.animating) return;
        // Navigating from a zoomed picture unzooms it first: the strip only
        // makes sense at life size, and the next picture arrives at life size
        // anyway. Placed as well as reset so the DOM never keeps a scale the
        // state has already dropped.
        if (this.zoom.s !== 1) {
          this.resetZoom();
          this.place(0, 0);
        }
        // Wrap-around, for the same reason the stage chips wrap: a dead control
        // at each end reads as a fault rather than as an edge.
        const photos = this.photos();
        if (!photos.length) return;
        const at = photos.indexOf(this.slide);
        const target = photos[((at === -1 ? 0 : at) + direction + photos.length) % photos.length];
        // An arrow tap IS a navigation, so it retires the swipe cue.
        this.learn('swipe');
        if (!this.animateStep(direction, target)) this.paint(target, photos);
      }

      // true means "handled": either the strip is sliding, or a slide is
      // already running and this call is deliberately swallowed. false means
      // the caller should repaint instantly instead.
      animateStep(direction, target) {
        if (!this.prevBuf || !this.nextBuf || this.mqStill.matches) return false;
        if (this.animating) return true;
        this.animating = true;
        this.stopNudge();
        this.glide(true);
        this.place(direction * -this.stageWidth(), 0);
        this.fadeScrim(null);
        this.stepTimer = window.setTimeout(() => {
          this.stepTimer = null;
          if (!this.isOpen) return;
          this.animating = false;
          this.glide(false);
          // The filter can have moved underneath us mid-slide, so the target is
          // re-checked against the list as it is now.
          const list = this.photos();
          if (list.indexOf(target) !== -1) this.paint(target, list);
          else if (list.length) this.paint(list[0], list);
        }, FD_LB_SLIDE_MS + 20);
        return true;
      }

      /* The listeners sit on the stage, but the stage is not a hit target: the
         events reach it by bubbling from the pictures, which are the only part
         of the overlay that opts into hit testing. That is deliberate -- it
         keeps a tap on the dark surround a scrim tap, which closes. */
      wireGestures() {
        this.stage.addEventListener('pointerdown', (event) => this.onDown(event));
        this.stage.addEventListener('pointermove', (event) => this.onMove(event));
        this.stage.addEventListener('pointerup', (event) => this.onUp(event));
        this.stage.addEventListener('pointercancel', (event) => this.onUp(event));
        this.gesturesOn = true;
      }

      onDown(event) {
        if (!this.isOpen || this.animating) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        // A primary pointer is by definition the first of its gesture, so any
        // entry still in the map is a release that never arrived (a finger
        // lifted over a element that had gone). Self-healing beats bookkeeping.
        if (event.isPrimary) this.pointers.clear();
        // A third finger has nothing to say, and it must not even be recorded:
        // in the map it would break the pinch's two-pointer gate (freezing a
        // live zoom under a resting palm) and then, when one of the two real
        // fingers lifted, let the pinch resume against the swapped-in one --
        // measured from the original pair, so the picture would jump. A pinch
        // stays keyed to the two fingers that opened it.
        if (this.pointers.size >= 2 && !this.pointers.has(event.pointerId)) return;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        // Two fingers are a pinch, whatever the first one had started: the
        // half-made drag is abandoned rather than committed, because the
        // gesture the shopper is making now is the other one.
        if (this.pointers.size === 2) {
          const [first, second] = Array.from(this.pointers.values());
          this.drag = null;
          this.place(0, 0);
          this.fadeScrim(null);
          this.pinch = {
            dist: Math.max(1, Math.hypot(first.x - second.x, first.y - second.y)),
            s: this.zoom.s,
            tx: this.zoom.tx,
            ty: this.zoom.ty,
            cx: (first.x + second.x) / 2,
            cy: (first.y + second.y) / 2
          };
          // The second finger needs capture for the same reason the first one
          // does, and more so: a pinch opens outwards, and without it the
          // finger that leaves the picture stops reporting and the zoom sticks.
          try {
            event.target.setPointerCapture(event.pointerId);
          } catch (error) {
            /* not fatal */
          }
          this.hideHints();
          this.stopNudge();
          return;
        }
        // First finger wins: a second touch landing mid-drag (a resting thumb)
        // must not hijack the gesture the first one is still making.
        if (this.drag) return;
        const now = window.performance && performance.now ? performance.now() : Date.now();
        this.drag = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          dx: 0,
          dy: 0,
          axis: null,
          vx: 0,
          lastX: event.clientX,
          lastT: now
        };
        // Zoomed in, one finger moves the picture inside its own frame instead
        // of moving the strip: neither a swipe nor a pull-to-close can be meant
        // while there is still picture off the edge of the screen to look at.
        if (this.zoom.s > 1) {
          this.drag.mode = 'pan';
          this.drag.tx0 = this.zoom.tx;
          this.drag.ty0 = this.zoom.ty;
        }
        this.glide(false);
        // Capture keeps the gesture alive when the finger leaves the picture;
        // without it the drag would simply end there, which is survivable.
        try {
          event.target.setPointerCapture(event.pointerId);
        } catch (error) {
          /* not fatal */
        }
        // Touching the picture answers the cue, but it is not yet a
        // navigation, so this hides without recording.
        this.hideHints();
        this.stopNudge();
      }

      onMove(event) {
        const held = this.pointers.get(event.pointerId);
        if (held) {
          held.x = event.clientX;
          held.y = event.clientY;
        }
        // Scaling about the midpoint between the fingers: the spot they are
        // pinching stays under them, and the midpoint moving is a pan for free.
        if (this.pinch && this.pointers.size === 2) {
          const [first, second] = Array.from(this.pointers.values());
          const dist = Math.max(1, Math.hypot(first.x - second.x, first.y - second.y));
          const cx = (first.x + second.x) / 2;
          const cy = (first.y + second.y) / 2;
          const scale = Math.min(FD_LB_ZOOM_MAX, Math.max(1, (this.pinch.s * dist) / this.pinch.dist));
          const factor = scale / this.pinch.s;
          const halfW = this.stageWidth() / 2;
          const halfH = this.stageHeight() / 2;
          this.zoom.s = scale;
          this.zoom.tx = cx - halfW + (this.pinch.tx - (this.pinch.cx - halfW)) * factor;
          this.zoom.ty = cy - halfH + (this.pinch.ty - (this.pinch.cy - halfH)) * factor;
          this.clampZoom();
          this.place(0, 0);
          return;
        }
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        drag.dx = event.clientX - drag.x;
        drag.dy = event.clientY - drag.y;
        // A pan never reaches the axis lock, the strip or the scrim fade: it is
        // one picture moving inside the window and nothing else.
        if (drag.mode === 'pan') {
          this.zoom.tx = drag.tx0 + drag.dx;
          this.zoom.ty = drag.ty0 + drag.dy;
          this.clampZoom();
          this.place(0, 0);
          return;
        }
        const now = window.performance && performance.now ? performance.now() : Date.now();
        if (now - drag.lastT > 16) {
          drag.vx = (event.clientX - drag.lastX) / (now - drag.lastT);
          drag.lastX = event.clientX;
          drag.lastT = now;
        }
        // The first few pixels decide what the gesture IS. A pull that is
        // allowed to be both reads as neither.
        if (!drag.axis && (Math.abs(drag.dx) > FD_LB_AXIS_LOCK || Math.abs(drag.dy) > FD_LB_AXIS_LOCK)) {
          drag.axis = Math.abs(drag.dx) > Math.abs(drag.dy) ? 'x' : 'y';
        }
        if (drag.axis === 'y' && drag.dy > 0) {
          // Pull down to put it away: the picture comes with the finger and the
          // backdrop thins, so the page behind starts showing through.
          this.place(0, drag.dy);
          this.fadeScrim(Math.max(0.35, 1 - drag.dy / 400));
        } else if (drag.axis === 'x' && this.prevBuf) {
          this.place(drag.dx, 0);
        }
      }

      onUp(event) {
        // A pinch ends when it stops being two fingers. The finger still down
        // does NOT become a drag: that would swipe a slide away on the release
        // of a zoom. It has to be lifted and put back.
        if (this.pinch) {
          this.pointers.delete(event.pointerId);
          if (this.pointers.size < 2) {
            this.pinch = null;
            this.clampZoom();
            this.place(0, 0);
          }
          return;
        }
        this.pointers.delete(event.pointerId);
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        this.drag = null;
        if (this.doubleTap(event, drag)) return;
        // A pan has nothing to commit: the picture is already where it was left.
        if (drag.mode === 'pan') return;
        if (drag.axis === 'y' && drag.dy > FD_LB_CLOSE_DROP) {
          this.closeSlide();
          return;
        }
        if (drag.axis === 'x' && this.prevBuf) {
          // Far enough, or fast enough: a flick is a short pull that means it.
          const flick = Math.abs(drag.vx) > FD_LB_FLICK && Math.abs(drag.dx) > FD_LB_AXIS_LOCK;
          if (Math.abs(drag.dx) > this.stageWidth() * FD_LB_COMMIT_RATIO || flick) {
            this.step(drag.dx < 0 ? 1 : -1);
            return;
          }
        }
        // Spring back to centre. Reduced motion wants the same end state without
        // the 240ms slide, so the transition is only armed when motion is welcome.
        if (!this.mqStill.matches) this.glide(true);
        this.place(0, 0);
        this.fadeScrim(null);
      }

      /* Two quick taps in the same place: in to a comfortable reading size at
         the spot tapped, or straight back out if it is already in. Touch only,
         because a mouse has the chips and a double-click on a photograph is not
         a gesture anyone makes here. A release that moved is not a tap, and it
         also forgets the first one -- a tap and a swipe are not a pair. True
         means the release is spent, so the swipe and close logic is skipped;
         false leaves the caller's own path untouched, which is what keeps a
         single tap behaving exactly as it always has. */
      doubleTap(event, drag) {
        if (event.pointerType === 'mouse') return false;
        // A cancelled gesture is not half a double-tap: leaving the first tap
        // on record would let it pair with an unrelated one 300ms later.
        if (event.type === 'pointercancel') {
          this.lastTap = null;
          return false;
        }
        const now = window.performance && performance.now ? performance.now() : Date.now();
        if (Math.abs(drag.dx) > FD_LB_AXIS_LOCK || Math.abs(drag.dy) > FD_LB_AXIS_LOCK) {
          this.lastTap = null;
          return false;
        }
        const last = this.lastTap;
        const paired =
          !!last &&
          now - last.t < FD_LB_DBL_MS &&
          Math.hypot(event.clientX - last.x, event.clientY - last.y) < FD_LB_DBL_SLOP;
        if (!paired) {
          this.lastTap = { t: now, x: event.clientX, y: event.clientY };
          return false;
        }
        this.lastTap = null;
        if (this.zoom.s > 1) this.resetZoom();
        else this.zoomTo(FD_LB_ZOOM_DBL, event.clientX, event.clientY);
        this.glide(false);
        this.place(0, 0);
        return true;
      }

      paint(slide, photos) {
        if (!slide || !this.lbImg(slide)) return;
        this.slide = slide;
        // A new picture arrives at life size, whatever was done to the last one.
        this.resetZoom();

        const many = photos.length > 1;
        const at = photos.indexOf(slide);
        const from = at === -1 ? 0 : at;
        const width = this.stageWidth();

        // Three buffers so the neighbour is already on the strip when the
        // finger starts pulling: the picture that peeks in IS the cue that
        // there is more. With one photograph there are no neighbours to make.
        // A picture already on the strip is PROMOTED rather than re-cloned:
        // a fresh clone starts life unpainted and Safari decodes it off the
        // main thread, so re-cloning what was already on screen flashed the
        // stage blank -- at the end of every slide, and for the whole swap
        // under reduced motion. The promoted element never drops its pixels.
        const pool = [this.prevBuf, this.cur, this.nextBuf].filter(Boolean);
        const take = (target, tx, low) => {
          const kept = pool.find((buffer) => buffer.dataset.fdLbFor === target.dataset.mediaId);
          if (!kept) return this.copy(target, tx, low);
          pool.splice(pool.indexOf(kept), 1);
          kept.style.transition = 'none';
          kept.style.transform = `translate3d(${tx}px, 0, 0)`;
          if (low) kept.setAttribute('fetchpriority', 'low');
          else kept.removeAttribute('fetchpriority');
          return kept;
        };
        this.cur = take(slide, 0, false);
        this.prevBuf = many ? take(photos[(from - 1 + photos.length) % photos.length], -width, true) : null;
        this.nextBuf = many ? take(photos[(from + 1) % photos.length], width, true) : null;
        this.stage.replaceChildren.apply(
          this.stage,
          [this.prevBuf, this.cur, this.nextBuf].filter(Boolean)
        );

        if (this.count) {
          this.count.textContent = `${photos.indexOf(slide) + 1} / ${photos.length}`;
          this.count.hidden = !many;
        }
        // One photograph has nothing to page through.
        if (this.prev) this.prev.hidden = !many;
        if (this.next) this.next.hidden = !many;

        // The cue pills ride the picture's own box, which is not measurable
        // until it has decoded.
        this.positionHints();
        if (!this.cur.complete) this.cur.addEventListener('load', () => this.positionHints(), { once: true });
        if (this.igRail && !this.rail.hidden) this.markRail();
      }

      // Built from the PAGE's rail so both strips show the same pictures in
      // the same order; a slide with no rail cell falls back to its own
      // picture. The 208px candidate is already in cache, so this costs nothing.
      buildRail(photos) {
        if (!this.rail) return;
        const cells = photos.map((slide) => {
          const cell = this.gallery.querySelector(`[data-target="${slide.dataset.mediaId}"]`);
          const source = cell?.querySelector('img') || this.lbImg(slide);
          const item = document.createElement('li');
          item.className = 'fd-lightbox__railitem';
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'fd-lightbox__railbtn';
          button.dataset.fdLbGo = slide.dataset.mediaId;
          // The page cell's accessible name, verbatim -- it is merchant copy
          // and product data, and nothing new is written here.
          const name = cell?.querySelector('button')?.textContent.trim();
          if (name) button.setAttribute('aria-label', name);
          if (source) {
            const copy = source.cloneNode(true);
            copy.className = 'fd-lightbox__railim';
            copy.removeAttribute('loading');
            copy.setAttribute('sizes', '208px');
            copy.alt = '';
            copy.draggable = false;
            button.appendChild(copy);
          }
          item.appendChild(button);
          return item;
        });
        this.rail.replaceChildren.apply(this.rail, cells);
        // One photograph has no strip to offer.
        this.rail.hidden = cells.length < 2;
      }

      markRail() {
        const id = this.slide?.dataset.mediaId;
        let current = null;
        this.rail.querySelectorAll('[data-fd-lb-go]').forEach((button) => {
          if (button.dataset.fdLbGo === id) {
            button.setAttribute('aria-current', 'true');
            current = button;
          } else {
            button.removeAttribute('aria-current');
          }
        });
        if (!current) return;
        const left = current.offsetLeft - (this.rail.clientWidth - current.offsetWidth) / 2;
        this.rail.scrollTo({
          left: Math.max(0, left),
          behavior: this.mqStill.matches ? 'auto' : 'smooth'
        });
      }

      // A buffer is the slide's own picture (lbImg's, so a dims slide brings
      // the plate that is actually on show), told it now fills the window so the
      // browser picks the largest candidate of the srcset the slide already
      // carries instead of the 60vw one the stage asked for. It is the picture
      // being looked at, so it is not deferred -- but the two neighbours are
      // speculative, and asking for them at low priority keeps them out of the
      // way of the one on screen.
      copy(slide, tx, low) {
        const source = this.lbImg(slide);
        const copy = source.cloneNode(true);
        copy.className = 'fd-lightbox__img';
        copy.dataset.fdLbFor = slide.dataset.mediaId;
        copy.removeAttribute('loading');
        copy.draggable = false;
        if (low) copy.setAttribute('fetchpriority', 'low');
        // The stage's candidate first, the window's after. A clone starts
        // life unpainted, and asking it straight for the 100vw candidate
        // left the stage black until that fetch and decode landed -- the
        // open-void. Keeping the slide's own sizes makes the first paint a
        // cache hit, and upgrading after it swaps the sharper candidate in
        // without dropping a frame, because an <img> keeps showing what it
        // has while it loads what it was asked for. A source that never
        // loaded has nothing worth preserving, so it asks big straight away.
        if (source.complete && source.naturalWidth) {
          const upgrade = () => copy.setAttribute('sizes', '100vw');
          if (copy.complete && copy.naturalWidth) upgrade();
          else copy.addEventListener('load', upgrade, { once: true });
        } else {
          copy.setAttribute('sizes', '100vw');
        }
        copy.style.transform = `translate3d(${tx}px, 0, 0)`;
        return copy;
      }

      stageWidth() {
        return this.stage.clientWidth || window.innerWidth || 1;
      }

      stageHeight() {
        return this.stage.clientHeight || window.innerHeight || 1;
      }

      // The strip is placed, never scrolled: one offset moves all three. Only
      // the picture being looked at carries the zoom -- the neighbours are the
      // strip, and scaling them would move where they sit.
      place(dx, dy) {
        const width = this.stageWidth();
        const zoom = this.zoom;
        if (this.prevBuf) this.prevBuf.style.transform = `translate3d(${dx - width}px, ${dy}px, 0)`;
        if (this.cur) {
          this.cur.style.transform =
            zoom && zoom.s > 1
              ? `translate3d(${dx + zoom.tx}px, ${dy + zoom.ty}px, 0) scale(${zoom.s})`
              : `translate3d(${dx}px, ${dy}px, 0)`;
        }
        if (this.nextBuf) this.nextBuf.style.transform = `translate3d(${dx + width}px, ${dy}px, 0)`;
      }

      // Back to life size. Everything that changes the picture goes through
      // here, because a pan carried into a different photograph -- or into the
      // same one at a new window size -- points at nothing.
      resetZoom() {
        this.zoom = { s: 1, tx: 0, ty: 0 };
        this.pinch = null;
      }

      /* The picture may not be dragged into the letterbox: the offset is capped
         at however much of the enlarged picture is hanging off the stage, which
         is zero while it still fits, so a photograph that is fully visible
         simply stays centred. The box is read off the layout (offsetWidth is
         the untransformed size, unlike a bounding rect) so it is never the
         previous scale's answer. */
      clampZoom() {
        const zoom = this.zoom;
        if (zoom.s <= FD_LB_ZOOM_SNAP) {
          zoom.s = 1;
          zoom.tx = 0;
          zoom.ty = 0;
          return;
        }
        if (zoom.s > FD_LB_ZOOM_MAX) zoom.s = FD_LB_ZOOM_MAX;
        const boxW = (this.cur && this.cur.offsetWidth) || this.stageWidth();
        const boxH = (this.cur && this.cur.offsetHeight) || this.stageHeight();
        const maxX = Math.max(0, (boxW * zoom.s - this.stageWidth()) / 2);
        const maxY = Math.max(0, (boxH * zoom.s - this.stageHeight()) / 2);
        zoom.tx = Math.min(maxX, Math.max(-maxX, zoom.tx));
        zoom.ty = Math.min(maxY, Math.max(-maxY, zoom.ty));
      }

      /* Zoom about a point on the screen: whatever was under it stays under it.
         The picture is centred on the stage and scales about its own middle, so
         a screen point is worked in offsets from the stage centre, and the new
         offset is the old one moved with the scale. */
      zoomTo(scale, cx, cy) {
        const zoom = this.zoom;
        const factor = scale / zoom.s;
        const ox = cx - this.stageWidth() / 2;
        const oy = cy - this.stageHeight() / 2;
        zoom.tx = ox + (zoom.tx - ox) * factor;
        zoom.ty = oy + (zoom.ty - oy) * factor;
        zoom.s = scale;
        this.clampZoom();
      }

      glide(on) {
        [this.prevBuf, this.cur, this.nextBuf].forEach((buffer) => {
          if (buffer) buffer.style.transition = on ? `transform ${FD_LB_SLIDE_MS}ms ease` : 'none';
        });
      }

      // null restores the stylesheet's own value -- and drops the exit
      // transition closeSlide armed, or the next drag's thinning would ease
      // when it should track the finger.
      fadeScrim(value) {
        if (!this.scrim) return;
        if (value === null) {
          this.scrim.style.opacity = '';
          this.scrim.style.transition = '';
        } else {
          this.scrim.style.opacity = String(value);
        }
      }

      stopNudge() {
        if (this.cur) this.cur.classList.remove('is-nudging');
      }

      /* Both pills sit just above the photograph, where a thumb resting near the
         bottom edge cannot cover them -- the user's ruling, and the reason this
         is measured rather than a fixed offset. The picture is centred and
         contained, so its own box gives the letterbox depth directly; when that
         depth is too shallow to hold a pill (landscape, where the picture
         nearly fills the window) they clamp to just under the close chip's
         row instead. */
      positionHints() {
        if (!this.hintSwipe && !this.hintRotate) return;
        const stageH = this.stage.clientHeight || window.innerHeight;
        const stageW = this.stageWidth();
        let imgH = this.cur ? this.cur.getBoundingClientRect().height : 0;
        if (!imgH && this.cur && this.cur.naturalWidth && this.cur.naturalHeight) {
          imgH = Math.min(stageH, stageW / (this.cur.naturalWidth / this.cur.naturalHeight));
        }
        if (!imgH) imgH = Math.min(stageH, stageW / 2);
        const base = stageH / 2 + imgH / 2 + 14;
        // 52px of close chip (1.2rem top + 4rem tall), a breath, and a pill.
        const cap = Math.max(12, stageH - 97);
        let rotateBottom = Math.min(base + 45, cap);
        let swipeBottom = Math.min(base, cap);
        const rotateOn = !!this.hintRotate && this.hintRotate.classList.contains('is-on');
        if (rotateOn && rotateBottom - swipeBottom < 45) swipeBottom = Math.max(12, rotateBottom - 45);
        if (this.hintSwipe) this.hintSwipe.style.bottom = `${swipeBottom}px`;
        if (this.hintRotate) this.hintRotate.style.bottom = `${rotateBottom}px`;
      }

      /* Shown on every open until the visitor navigates once, then never again.
         Both are touch-only: a mouse has no phone to turn and no strip to pull,
         and the swipe cue needs somewhere to swipe to. */
      showHints() {
        this.clearHints();
        if (!this.mqCoarse.matches) return;
        const many = this.photos().length > 1;
        if (this.hintSwipe && many && !fdLbLearnt(FD_LB_KEY_SWIPE)) {
          this.hintSwipe.classList.add('is-on');
          this.timers.push(window.setTimeout(() => this.hintSwipe.classList.remove('is-on'), FD_LB_SWIPE_MS));
          if (!this.mqStill.matches && this.cur) {
            // The picture itself leans and comes back: the gesture demonstrated
            // rather than described.
            this.cur.classList.add('is-nudging');
            this.timers.push(window.setTimeout(() => this.stopNudge(), FD_LB_NUDGE_MS));
          }
        }
        if (this.hintRotate && many && this.mqPortrait.matches && !fdLbLearnt(FD_LB_KEY_ROTATE)) {
          this.hintRotate.classList.add('is-on');
          this.timers.push(window.setTimeout(() => this.hintRotate.classList.remove('is-on'), FD_LB_ROTATE_MS));
        }
        this.positionHints();
      }

      // Off the screen, nothing recorded: the cue may still be owed.
      hideHints() {
        if (this.hintSwipe) this.hintSwipe.classList.remove('is-on');
        if (this.hintRotate) this.hintRotate.classList.remove('is-on');
        this.stopNudge();
      }

      clearHints() {
        this.timers.forEach((timer) => window.clearTimeout(timer));
        this.timers = [];
        this.hideHints();
      }

      /* The cue is spent the moment the shopper does the thing it asks for --
         and an arrow tap counts as having navigated, so the swipe cue is not
         owed a second showing to someone who has already moved between
         photographs. */
      learn(which) {
        if (which === 'swipe') {
          fdLbLearn(FD_LB_KEY_SWIPE);
          if (this.hintSwipe) this.hintSwipe.classList.remove('is-on');
          this.stopNudge();
          return;
        }
        fdLbLearn(FD_LB_KEY_ROTATE);
        if (this.hintRotate) this.hintRotate.classList.remove('is-on');
      }

      focusables() {
        return window.FdChooser?.focusables ? window.FdChooser.focusables(this) : [];
      }

      focusFirst() {
        const items = this.focusables();
        if (items.length) items[0].focus();
      }

      keydown(event) {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
          return;
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(event.key === 'ArrowLeft' ? -1 : 1);
          return;
        }
        if (event.key !== 'Tab') return;
        const items = this.focusables();
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );

  /* The editor throws a section's DOM away and renders a fresh one. A lightbox
     that has been portaled to <body> is no longer in that DOM, so it would
     survive the swap as an orphan -- holding a dead gallery reference, and
     stranding the scroll lock if it was open. Dropping it here is enough: the
     replacement section ships its own, and disconnecting releases the lock and
     the document keydown listener. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:unload', function (event) {
      document.querySelectorAll('body > fd-lightbox').forEach(function (box) {
        if (box.sectionId && box.sectionId === event.target.id) box.remove();
      });
    });
  }
}
